import { FIRST_SCENE_SLUG } from "./firstScene";

export type InterpreterSpeaker = "you" | "them";

export type InterpreterTurn = {
  role: "user" | "assistant";
  speaker: InterpreterSpeaker;
  text: string;
  timestamp: Date;
};

export const INTERPRETER_KIND = "interpreter";
export const INTERPRETER_CONTEXT_TITLE = "Ajuda ao vivo";

export const INTERPRETER_VOICE_CONTEXT = {
  title: INTERPRETER_CONTEXT_TITLE,
  subtitle: "Intérprete no celular",
  level: "beginner" as const,
  aiRole: "interpreter",
  studentGoal: "Traduzir o que for dito",
};

export const INTERPRETER_NOTICE =
  "A pessoa do outro lado precisa saber que o celular está traduzindo em voz alta.";

type SceneHint = {
  slug: string;
  title: string;
  terms: string[];
};

export const INTERPRETER_SCENE_HINTS: SceneHint[] = [
  {
    slug: "viagem-checkin-aeroporto-beginner",
    title: "No balcão do aeroporto",
    terms: ["aeroporto", "airport", "boarding", "check-in", "checkin", "passaporte", "passport", "gate", "voo", "flight", "embarque"],
  },
  {
    slug: "comida-restaurante-mesa-beginner",
    title: "Mesa no restaurante",
    terms: ["restaurante", "restaurant", "mesa", "table", "cardápio", "cardapio", "menu", "garçom", "garcom", "waiter"],
  },
  {
    slug: "trabalho-reuniao-standup-beginner",
    title: "Stand-up da manhã",
    terms: ["reunião", "reuniao", "meeting", "standup", "stand-up", "update", "sprint", "bloqueio"],
  },
  {
    slug: "viagem-hotel-checkin-beginner",
    title: "Na recepção do hotel",
    terms: ["hotel", "quarto", "room", "reserva", "reservation", "hóspede", "hospede"],
  },
  {
    slug: "saude-consulta-medico-beginner",
    title: "Consulta no médico",
    terms: ["médico", "medico", "doctor", "dor", "pain", "consulta", "farmácia", "farmacia"],
  },
  {
    slug: "transporte-uber-beginner",
    title: "Corrida de Uber",
    terms: ["uber", "corrida", "motorista", "driver", "destino", "airport please", "terminal"],
  },
];

export function interpreterTranscriptText(turns: Array<{ text?: string }>): string {
  return turns
    .map((item) => (item.text ?? "").replace(/\[final\]/g, "").trim())
    .filter(Boolean)
    .join(" ");
}

export function matchSqueezeToScene(
  text: string,
  fallback = FIRST_SCENE_SLUG,
): { slug: string; title: string; hits: number } {
  const hay = text.toLowerCase();
  let best = { slug: fallback, title: "No balcão do aeroporto", hits: 0 };
  const fallbackHint = INTERPRETER_SCENE_HINTS.find((row) => row.slug === fallback);
  if (fallbackHint) best = { slug: fallbackHint.slug, title: fallbackHint.title, hits: 0 };

  for (const row of INTERPRETER_SCENE_HINTS) {
    const hits = row.terms.filter((term) => hay.includes(term)).length;
    if (hits > best.hits) {
      best = { slug: row.slug, title: row.title, hits };
    }
  }
  return best;
}

export function applyInterpreterDelta(
  prev: InterpreterTurn[],
  role: InterpreterTurn["role"],
  speaker: InterpreterSpeaker,
  delta: string,
  now: Date = new Date(),
): InterpreterTurn[] {
  const last = prev[prev.length - 1];
  if (last?.role === role && last.speaker === speaker) {
    return [
      ...prev.slice(0, -1),
      { ...last, text: last.text + delta },
    ];
  }
  return [...prev, { role, speaker, text: delta, timestamp: now }];
}

export function visibleInterpreterLines(turns: InterpreterTurn[]): InterpreterTurn[] {
  return turns
    .map((item) => ({
      ...item,
      text: item.text.replace(/\[final\]/g, "").trim(),
    }))
    .filter((item) => item.text);
}

export function speakerLabel(speaker: InterpreterSpeaker): string {
  return speaker === "you" ? "Você" : "Eles";
}

export function canCaptureWhileHolding(ready: boolean, holding: InterpreterSpeaker | null): boolean {
  return ready && holding !== null;
}

export type InterpreterTalkAction = "start" | "stop" | "switch";

export function nextInterpreterTalk(
  current: InterpreterSpeaker | null,
  clicked: InterpreterSpeaker,
): { action: InterpreterTalkAction; speaker: InterpreterSpeaker | null } {
  if (current === null) return { action: "start", speaker: clicked };
  if (current === clicked) return { action: "stop", speaker: null };
  return { action: "switch", speaker: clicked };
}

export function interpreterStatusLabel(
  status: "idle" | "connecting" | "ready" | "holding" | "speaking",
  recording: boolean,
): string {
  if (status === "connecting") return "Conectando…";
  if (recording || status === "holding") return "Gravando…";
  if (status === "speaking") return "A traduzir…";
  return "Toca para gravar. Toca de novo para traduzir.";
}

export function isInterpreterAnalysis(raw: unknown): boolean {
  return Boolean(raw && typeof raw === "object" && (raw as { kind?: unknown }).kind === INTERPRETER_KIND);
}

export function stagePathForSlug(slug: string): string {
  return `/app/practice/${slug}`;
}

export function mobileStagePathForSlug(slug: string): string {
  return `/cena/${slug}`;
}

export type InterpreterPersistRow = {
  user_id: string;
  situation_id: string | null;
  catalog_slug: string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  status: "ready";
  transcript: Array<{
    role: "user" | "assistant";
    speaker: InterpreterSpeaker;
    text: string;
    timestamp: string;
  }>;
  analysis: {
    kind: typeof INTERPRETER_KIND;
    matchedSlug: string;
    source: "ajuda";
  };
};

export function buildInterpreterSessionRow(input: {
  userId: string;
  startedAt: number;
  endedAt?: number;
  transcripts: InterpreterTurn[];
  situationId?: string | null;
}): {
  row: InterpreterPersistRow;
  matched: { slug: string; title: string; hits: number };
  billedMinutes: number;
  situationTitle: string;
  situationDescription: string;
} {
  const endedAt = input.endedAt ?? Date.now();
  const durationSeconds = Math.max(0, Math.round((endedAt - input.startedAt) / 1000));
  const cleaned = visibleInterpreterLines(input.transcripts);
  const matched = matchSqueezeToScene(interpreterTranscriptText(cleaned));
  const transcript = cleaned.map((item) => ({
    role: item.role,
    speaker: item.speaker,
    text: item.text,
    timestamp: item.timestamp.toISOString(),
  }));

  return {
    matched,
    billedMinutes: Math.max(1, Math.ceil(durationSeconds / 60)),
    situationTitle: `Aperto: ${matched.title}`,
    situationDescription: cleaned
      .slice(0, 12)
      .map((item) => `${speakerLabel(item.speaker)}: ${item.text}`)
      .join("\n")
      .slice(0, 800) || INTERPRETER_CONTEXT_TITLE,
    row: {
      user_id: input.userId,
      situation_id: input.situationId ?? null,
      catalog_slug: matched.slug,
      started_at: new Date(input.startedAt).toISOString(),
      ended_at: new Date(endedAt).toISOString(),
      duration_seconds: durationSeconds,
      status: "ready",
      transcript,
      analysis: {
        kind: INTERPRETER_KIND,
        matchedSlug: matched.slug,
        source: "ajuda",
      },
    },
  };
}
